import { useCallback, useEffect, useState } from "react";
import { ArrowDownToLine, ChevronRight } from "lucide-react";
import { Modal, StatusPill } from "../components/common";
import { api, date, errMsg, idr } from "../lib/api";

const PROVIDERS = { bank: ["BCA", "BRI", "BNI", "Mandiri", "BSI", "CIMB Niaga", "Permata", "Lainnya"], ewallet: ["DANA", "OVO", "GoPay", "ShopeePay", "LinkAja"] };

const WithdrawModal = ({ balance, onClose, onDone }) => {
  const [form, setForm] = useState({ method: "bank", provider: "BCA", account_number: "", account_name: "", amount: balance.available });
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      await api.post("/affiliates/withdrawals", { ...form, amount: Number(form.amount) });
      onDone();
    } catch (err) {
      setError(errMsg(err, "Penarikan gagal diajukan"));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Modal onClose={onClose} testId="withdraw-modal">
      <form onSubmit={submit}>
        <p className="eyebrow">PENARIKAN KOMISI</p>
        <h3>Tarik ke rekening / e-wallet</h3>
        <p className="muted">Saldo tersedia <b>{idr(balance.available)}</b> · minimal {idr(balance.minimum)}</p>
        <div className="seg">
          {[["bank", "Rekening bank"], ["ewallet", "E-wallet"]].map(([v, l]) => (
            <button type="button" key={v} className={form.method === v ? "active" : ""} onClick={() => setForm({ ...form, method: v, provider: PROVIDERS[v][0] })} data-testid={`withdraw-method-${v}`}>{l}</button>
          ))}
        </div>
        <label>{form.method === "bank" ? "Nama bank" : "E-wallet"}
          <select value={form.provider} onChange={set("provider")} data-testid="withdraw-provider-select">{PROVIDERS[form.method].map((p) => <option key={p}>{p}</option>)}</select>
        </label>
        <label>{form.method === "bank" ? "Nomor rekening" : "Nomor e-wallet"}<input required inputMode="numeric" pattern="[0-9]{6,20}" title="6-20 digit angka" value={form.account_number} onChange={set("account_number")} data-testid="withdraw-account-number-input" /></label>
        <label>Atas nama<input required value={form.account_name} onChange={set("account_name")} data-testid="withdraw-account-name-input" /></label>
        <label>Nominal (Rp)<input required type="number" min={balance.minimum} max={balance.available} value={form.amount} onChange={set("amount")} data-testid="withdraw-amount-input" /></label>
        {error && <div className="error" data-testid="withdraw-error">{error}</div>}
        <button className="primary full" disabled={busy} data-testid="withdraw-submit-button">{busy ? "Mengajukan…" : "Ajukan penarikan"} <ChevronRight size={16} /></button>
      </form>
    </Modal>
  );
};

export const WithdrawPanel = () => {
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(false);
  const [msg, setMsg] = useState("");
  const load = useCallback(() => api.get("/affiliates/withdrawals").then((r) => setData(r.data)), []);
  useEffect(() => { load(); }, [load]);
  const b = data?.balance;
  const hasPending = data?.items?.some((w) => w.status === "pending");
  const canWithdraw = b && b.available >= b.minimum && !hasPending;
  return (
    <section className="panel table-panel mt">
      <div className="panel-head">
        <div><p className="eyebrow">SALDO KOMISI</p><h3 data-testid="withdraw-available">{idr(b?.available)} tersedia</h3>
          <p className="muted">Diproses {idr(b?.pending)} · sudah ditarik {idr(b?.withdrawn)}</p></div>
        <button className="primary" disabled={!canWithdraw} onClick={() => setOpen(true)} data-testid="open-withdraw-button"
          title={hasPending ? "Masih ada penarikan yang diproses" : `Minimal ${idr(b?.minimum)}`}><ArrowDownToLine size={15} /> Tarik komisi</button>
      </div>
      {msg && <div className="notice" data-testid="withdraw-success-message">{msg}</div>}
      {data?.items?.length > 0 && (
        <>
          <div className="table-row table-head"><span>TANGGAL</span><span>TUJUAN</span><span>NOMINAL</span><span>STATUS</span></div>
          {data.items.map((w) => (
            <div className="table-row" key={w.id} data-testid={`withdraw-row-${w.id}`}>
              <span>{date(w.created_at)}</span><span>{w.provider} · {w.account_number}<small className="muted block">a.n. {w.account_name}</small></span>
              <span>{idr(w.amount)}</span><span className="row-end"><StatusPill status={w.status} />{w.review_note && <small className="muted">{w.review_note}</small>}</span>
            </div>
          ))}
        </>
      )}
      {open && <WithdrawModal balance={b} onClose={() => setOpen(false)} onDone={() => { setOpen(false); setMsg("Penarikan diajukan. Admin akan memproses dan Anda akan menerima notifikasi."); load(); }} />}
    </section>
  );
};
